<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Header Premium -->
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="fas fa-plus-circle"></i>
          </div>
          <div class="header-text">
            <h3>💸 Nouvelle Dépense</h3>
            <p>Créer une dépense journalière ou charge mensuelle</p>
          </div>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Contenu -->
      <div class="modal-body">
        <!-- Type de dépense -->
        <div class="type-section">
          <h4>📋 Type de Dépense</h4>
          <div class="type-buttons">
            <button 
              @click="form.type = 'journaliere'" 
              :class="{ active: form.type === 'journaliere' }"
              class="type-btn"
            >
              <i class="fas fa-calendar-day"></i>
              <span>Dépense Journalière</span>
              <small>Carburant, péage, divers...</small>
            </button>
            <button 
              @click="form.type = 'mensuelle'"
              :class="['type-btn', { active: form.type === 'mensuelle' }]"
            >
              <i class="fas fa-calendar-alt"></i>
              <span>Charge Mensuelle</span>
              <small>Loyer, internet, assurances...</small>
            </button>
          </div>
        </div>

        <!-- Informations de base -->
        <div class="info-section">
          <h4>ℹ️ Informations de Base</h4>
          
          <!-- Catégorie -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-tags"></i>
              Catégorie *
            </label>
            <select 
              v-model="form.categorie" 
              class="form-select" 
              required
              @change="updateDesignation"
            >
              <option value="">Sélectionner une catégorie</option>
              <template v-if="form.type === 'journaliere'">
                <option value="carburant">⛽ Carburant</option>
                <option value="peage">🛣️ Péage</option>
                <option value="divers">📋 Divers</option>
              </template>
              <template v-else>
                <option value="loyer">🏢 Loyer</option>
                <option value="internet">🌐 Internet</option>
                <option value="telephone">📞 Téléphone</option>
                <option value="hebergement_vps">☁️ Hébergement VPS</option>
                <option value="salaires">💼 Salaires</option>
                <option value="assurances">🛡️ Assurances</option>
                <option value="autre">📋 Autre</option>
              </template>
            </select>
          </div>

          <!-- Désignation -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-edit"></i>
              Désignation *
            </label>
            <input 
              v-model="form.designation"
              type="text" 
              class="form-input"
              placeholder="Description de la dépense..."
              required
            >
          </div>

          <!-- Montant et Date -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-euro-sign"></i>
                Montant (TND) *
              </label>
              <input 
                v-model.number="form.montant"
                type="number" 
                step="0.01"
                min="0"
                class="form-input"
                placeholder="0.00"
                required
              >
            </div>
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-calendar"></i>
                Date *
              </label>
              <input 
                v-model="form.date"
                type="date" 
                class="form-input"
                required
              >
            </div>
          </div>

          <!-- Fournisseur (optionnel) -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-store"></i>
              Fournisseur
            </label>
            <input 
              v-model="form.fournisseur"
              type="text" 
              class="form-input"
              placeholder="Nom du fournisseur ou magasin..."
            >
          </div>
        </div>

        <!-- Paiement -->
        <div class="paiement-section">
          <h4>💳 Mode de Paiement</h4>
          
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-credit-card"></i>
              Mode de paiement *
            </label>
            <select v-model="form.modePaiement" class="form-select" required>
              <option value="">Sélectionner un mode</option>
              <option value="cash_societe">💵 Cash société</option>
              <option value="avance_personnel">👤 Avance personnelle</option>
              <option value="virement">🏦 Virement</option>
              <option value="cheque">📝 Chèque</option>
              <option value="carte_bancaire">💳 Carte bancaire</option>
            </select>
          </div>

          <!-- Numéro facture (optionnel) -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-receipt"></i>
              Numéro de facture
            </label>
            <input 
              v-model="form.numeroFacture"
              type="text" 
              class="form-input"
              placeholder="Numéro de la facture..."
            >
          </div>
        </div>

        <!-- Justificatif -->
        <div class="justificatif-section">
          <h4>📎 Justificatif</h4>
          
          <div class="upload-zone" @click="$refs.fileInput.click()">
            <div v-if="!form.pieceJointe" class="upload-placeholder">
              <i class="fas fa-cloud-upload-alt"></i>
              <p>Cliquez pour ajouter une photo du ticket/facture</p>
              <small>JPG, PNG, PDF - Max 5MB</small>
            </div>
            <div v-else class="upload-preview">
              <i class="fas fa-file-image"></i>
              <p>{{ fileName }}</p>
              <button @click.stop="removeFile" class="btn-remove">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <input 
            ref="fileInput"
            type="file" 
            accept="image/*,application/pdf"
            @change="handleFileUpload"
            style="display: none"
          >
        </div>

        <!-- Récurrence (pour charges mensuelles) -->
        <div v-if="form.type === 'mensuelle'" class="recurrence-section">
          <h4>🔄 Récurrence</h4>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input 
                v-model="form.recurrence.actif"
                type="checkbox"
                class="form-checkbox"
              >
              <span class="checkmark"></span>
              Charge récurrente
            </label>
          </div>

          <div v-if="form.recurrence.actif" class="recurrence-details">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-repeat"></i>
                  Fréquence
                </label>
                <select v-model="form.recurrence.frequence" class="form-select">
                  <option value="mensuelle">📅 Mensuelle</option>
                  <option value="trimestrielle">📊 Trimestrielle</option>
                  <option value="annuelle">🗓️ Annuelle</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-calendar-day"></i>
                  Jour d'échéance
                </label>
                <input 
                  v-model.number="form.recurrence.jourEcheance"
                  type="number" 
                  min="1"
                  max="31"
                  class="form-input"
                  placeholder="1-31"
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Commentaires -->
        <div class="commentaires-section">
          <h4>💬 Commentaires</h4>
          <textarea 
            v-model="form.commentaires"
            class="form-textarea"
            rows="3"
            placeholder="Ajoutez des commentaires ou observations..."
          ></textarea>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn-cancel">
          <i class="fas fa-times"></i>
          Annuler
        </button>
        <button 
          @click="submitDepense" 
          :disabled="!canSubmit || loading"
          class="btn-submit"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-plus"></i>
          {{ loading ? 'Création...' : 'Créer la Dépense' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'CreateDepenseModal',
  emits: ['close', 'depense-created'],
  setup(props, { emit }) {
    const toast = useToast()
    const loading = ref(false)
    const fileName = ref('')
    const fileInput = ref(null)

    const form = ref({
      type: 'journaliere',
      categorie: '',
      designation: '',
      montant: 0,
      date: new Date().toISOString().split('T')[0],
      fournisseur: '',
      modePaiement: '',
      numeroFacture: '',
      pieceJointe: null,
      commentaires: '',
      recurrence: {
        actif: false,
        frequence: 'mensuelle',
        jourEcheance: 1
      }
    })

    const canSubmit = computed(() => {
      if (!form.value) return false
      
      return form.value.type && 
             form.value.categorie && 
             form.value.designation && 
             form.value.montant > 0 && 
             form.value.date && 
             form.value.modePaiement
    })

    const updateDesignation = () => {
      if (!form.value) return
      
      const suggestions = {
        // Journalières
        'carburant': 'Carburant véhicule de service',
        'peage': 'Péage autoroute',
        'divers': 'Dépense diverse',
        // Mensuelles
        'loyer': 'Loyer bureau',
        'internet': 'Abonnement internet',
        'telephone': 'Abonnement téléphonie',
        'hebergement_vps': 'Hébergement serveur',
        'salaires': 'Charges salariales',
        'assurances': 'Prime d\'assurance'
      }
      
      if (suggestions[form.value.categorie] && !form.value.designation) {
        form.value.designation = suggestions[form.value.categorie]
      }
    }

    const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()
        
        img.onload = () => {
          // Calculer les nouvelles dimensions
          let { width, height } = img
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }
          
          canvas.width = width
          canvas.height = height
          
          // Dessiner l'image redimensionnée
          ctx.drawImage(img, 0, 0, width, height)
          
          // Convertir en Base64 avec compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality)
          resolve(compressedDataUrl)
        }
        
        img.src = URL.createObjectURL(file)
      })
    }

    const handleFileUpload = async (event) => {
      const file = event.target.files[0]
      if (!file) return

      // Vérifier le type de fichier
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, GIF ou PDF.')
        return
      }

      // Vérifier la taille (10MB max avant compression)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 10MB')
        return
      }

      fileName.value = file.name

      try {
        // Compresser les images, garder les PDF tels quels
        if (file.type.startsWith('image/')) {
          const compressedImage = await compressImage(file)
          if (form.value) {
            form.value.pieceJointe = compressedImage
          }
          toast.success('Image compressée et prête à l\'envoi')
        } else {
          // Pour les PDF, convertir directement en Base64
          const reader = new FileReader()
          reader.onload = (e) => {
            if (form.value) {
              form.value.pieceJointe = e.target.result
            }
          }
          reader.readAsDataURL(file)
        }
      } catch (error) {
        console.error('Erreur compression:', error)
        toast.error('Erreur lors du traitement du fichier')
      }
    }

    const removeFile = () => {
      if (!form.value) return
      
      form.value.pieceJointe = null
      fileName.value = ''
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const submitDepense = async () => {
      if (!canSubmit.value || !form.value) return

      loading.value = true
      try {
        const token = localStorage.getItem('token')
        
        // Préparer les données selon le type
        const depenseData = {
          type: form.value.type,
          designation: form.value.designation,
          montant: form.value.montant,
          date: form.value.date,
          fournisseur: form.value.fournisseur,
          modePaiement: form.value.modePaiement,
          numeroFacture: form.value.numeroFacture,
          pieceJointe: form.value.pieceJointe,
          commentaires: form.value.commentaires
        }

        // Ajouter la catégorie selon le type
        if (form.value.type === 'journaliere') {
          depenseData.categorieJournaliere = form.value.categorie
        } else {
          depenseData.categorieMensuelle = form.value.categorie
          
          // Ajouter la récurrence si activée
          if (form.value.recurrence.actif) {
            depenseData.recurrence = form.value.recurrence
          }
        }

        console.log('=== ENVOI DÉPENSE FRONTEND ===')
        console.log('Données envoyées:', depenseData)
        
        const response = await fetch('/api/finance/depenses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(depenseData)
        })

        console.log('Réponse status:', response.status)
        
        if (response.ok) {
          const data = await response.json()
          console.log('Dépense créée avec succès:', data)
          toast.success('✅ Dépense créée avec succès')
          emit('depense-created', data.depense)
          closeModal()
        } else {
          const error = await response.json()
          console.error('Erreur création:', error)
          toast.error(error.message || 'Erreur lors de la création')
        }
      } catch (error) {
        console.error('Erreur création dépense:', error)
        
        // Gestion spécifique de l'erreur PayloadTooLargeError
        if (error.message && error.message.includes('PayloadTooLargeError')) {
          toast.error('Le fichier joint est trop volumineux. Réduisez la taille ou la qualité de l\'image.')
        } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          toast.error('Le fichier est trop volumineux pour être envoyé. Veuillez utiliser un fichier plus petit.')
        } else {
          toast.error('Erreur lors de la création de la dépense')
        }
      } finally {
        loading.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    return {
      form,
      loading,
      fileName,
      fileInput,
      canSubmit,
      updateDesignation,
      handleFileUpload,
      removeFile,
      submitDepense,
      closeModal
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Header */
.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 700;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

/* Body */
.modal-body {
  padding: 2rem;
}

/* Sections */
.type-section,
.info-section,
.paiement-section,
.justificatif-section,
.recurrence-section,
.commentaires-section {
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.type-section h4,
.info-section h4,
.paiement-section h4,
.justificatif-section h4,
.recurrence-section h4,
.commentaires-section h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1.1rem;
}

/* Type buttons */
.type-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.type-btn {
  background: white;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.type-btn:hover {
  border-color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.15);
}

.type-btn.active {
  border-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
}

.type-btn i {
  font-size: 2rem;
  color: var(--primary-blue);
}

.type-btn span {
  font-weight: 600;
  color: var(--primary-blue);
}

.type-btn small {
  color: #6c757d;
  font-size: 0.8rem;
}

/* Form elements */
.form-group {
  margin-bottom: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.form-label i {
  margin-right: 0.5rem;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
  transform: translateY(-2px);
}

/* Upload zone */
.upload-zone {
  border: 2px dashed rgba(0, 27, 183, 0.3);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.upload-zone:hover {
  border-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.02);
}

.upload-placeholder i {
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 1rem;
}

.upload-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.upload-preview i {
  font-size: 2rem;
  color: #10b981;
}

.btn-remove {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Checkbox */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 600;
  color: var(--primary-blue);
}

.form-checkbox {
  width: 20px;
  height: 20px;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 27, 183, 0.3);
  border-radius: 4px;
  position: relative;
}

.form-checkbox:checked + .checkmark {
  background: var(--primary-blue);
  border-color: var(--primary-blue);
}

.form-checkbox:checked + .checkmark::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: bold;
}

/* Recurrence details */
.recurrence-details {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
}

/* Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-radius: 0 0 20px 20px;
}

.btn-cancel,
.btn-submit {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-submit {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 1rem;
  }
  
  .type-buttons {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
